import { DebounceStack } from '../helpers/debounce-stack';
import { waitForTruthyVal } from '../helpers/utils';
import { updateCart, cartState } from './cart.service';
const DY_DELIMITER = "::";
const DY_CART_ATTR = "_dy_variations";
export const dialogStack = new DebounceStack(100);
dialogStack.addListener((stack) => {
    console.log('dialogStack listener', stack);
    const lastPopup = stack[stack.length - 1];
    if (!stack.length) {
        csqArtificialPageview('close', lastPopup);
    }
    else {
        csqArtificialPageview('open', lastPopup);
    }
});
/**
 * used to track page views in SPA
 */
export function trackPageView(route) {
    if (!route) {
        console.warn('route not defined; unable to track page view');
    }
    else {
        if (window.ga) {
            window.ga('set', 'page', route);
            window.ga('send', 'pageview');
        }
    }
}
export function emitShopifyEvent(name, payload = {}) {
    console.debug("emitting shopify event", name, payload);
    window.Shopify?.analytics?.publish(name, payload);
}
/**
 * sends an event to Dynamic Yield via DY.API
 * NOTE: we should try using the Experience API via web pixel before relying on this method
 */
export async function emitDynamicYieldEvent(name, payload = {}) {
    try {
        const api = await waitForTruthyVal(() => window.DY?.API);
        const eventData = {
            name: name,
            properties: payload
        };
        console.debug(`emitting dynamic yield event: ${JSON.stringify(eventData)}`);
        api('event', eventData);
    }
    catch (e) {
        console.warn("error with dy", e.message || e);
    }
}
/**
 * sends a single-page application event to Dynamic Yield
 * note: deprecated per DY docs: https://support.dynamicyield.com/hc/en-us/articles/360022956414--Deprecated-Single-Page-Applications-on-Websites
 */
export async function emitDynamicYieldSpaEvent(payload) {
    try {
        const api = await waitForTruthyVal(() => window.DY?.API);
        console.debug(`emitting dynamic yield SPA event: ${JSON.stringify(payload)}`);
        api('spa', payload);
    }
    catch (e) {
        console.warn("error with dy spa event", e.message || e);
    }
}
function updateCartAttributesIfChanged(attributes) {
    const existing = cartState.get()?.attributes ?? {};
    const hasChange = Object.entries(attributes).some(([key, value]) => existing[key] !== value);
    if (!hasChange) {
        return;
    }
    updateCart({ attributes });
}
export function attachSessionIdsToCart() {
    if (!window.UNTUCKitConfig.shopifyTestCartAttachSessionIdsToggle) {
        console.log("session IDs not attached to cart");
        return;
    }
    const pendingSessionAttributes = {};
    const flushSessionAttributes = () => {
        if (!Object.keys(pendingSessionAttributes).length) {
            return;
        }
        updateCartAttributesIfChanged({ ...pendingSessionAttributes });
    };
    getCSQGlobal().push(["getSessionKey", {
            callback: (sessionKey) => {
                if (sessionKey) {
                    pendingSessionAttributes._csq_session_key = sessionKey;
                    flushSessionAttributes();
                }
            }
        }]);
    waitForTruthyVal(getGA4Data, 500, 10)
        .then((data) => {
        const sessionParts = [...(data.sessionData ?? [])];
        pendingSessionAttributes.ga_session_id = sessionParts.shift() ?? '';
        pendingSessionAttributes.ga_session_number = sessionParts.shift() ?? '';
        pendingSessionAttributes.ga_client_id = data.clientId;
        flushSessionAttributes();
    }).catch(err => {
        console.warn("Could not get GA data.", err);
    });
    setInterval(addDyInfo, 5000);
}
function getGA4Data() {
    // Extract Session ID from the _ga_{measurement id} cookie
    const sessionPattern = new RegExp(`_ga_${window.UNTUCKitConfig.ga4MeasurementId}=GS\\d\\.\\d\\.(.+?)(?:;|$)`);
    const sessionMatch = document.cookie.match(sessionPattern);
    const sessionParts = sessionMatch?.[1].split(".");
    // Extract Client ID from the _ga cookie
    const clientIdPattern = new RegExp(`_ga=GA\\d\\.\\d\\.(\\d+\\.\\d+)(?:;|$)`);
    const clientIdMatch = document.cookie.match(clientIdPattern);
    const clientId = clientIdMatch?.[1];
    if (!sessionParts || !clientId) {
        return null;
    }
    return {
        clientId: clientId,
        sessionData: sessionParts
    };
}
function addDyInfo() {
    if (window.DYExps?.otags && window.DYO?.chosenVariations) {
        const currentDyVariations = Object.entries(window.DYO.chosenVariations)
            .filter(entry => !!entry[1].smartObject)
            .map(entry => {
            const [campaignId, campaignInfo] = entry;
            const campaignName = convertDyString(window.DYExps?.otags[campaignId].displayName);
            const experienceName = convertDyString(campaignInfo?.smartObject.name);
            if (!campaignName || !experienceName)
                return null;
            return campaignInfo.chosenVariations.map((variation) => `${campaignName}|${experienceName}|${convertDyString(variation.name)}`);
        }).flat().filter(v => !!v).sort();
        const existingDyVariations = (cartState.get()?.attributes?.[DY_CART_ATTR] || "").split(DY_DELIMITER).filter(s => !!s);
        if (!currentDyVariations.every(v => existingDyVariations.includes(v))) {
            const mergedDyVariations = Array.from(new Set(existingDyVariations.concat(currentDyVariations))).sort();
            console.log("updating DY info on cart", mergedDyVariations);
            updateCartAttributesIfChanged({
                [DY_CART_ATTR]: mergedDyVariations.join(DY_DELIMITER),
            });
        }
    }
}
/**
 * returns the Content Square global object. If it doesn't exist, it initializes it as an empty array.
 * @see: https://docs.contentsquare.com/en/web/#the-_uxa-object
 * @returns
 */
export function getCSQGlobal() {
    return window._uxa = window._uxa || [];
}
/**
 * Sends a custom page event to Contentsquare.
 * Use for binary presence signals (e.g. "did this page have feature X").
 * @see https://docs.contentsquare.com/en/web/custom-page-events/
 */
export function csqTrackPageEvent(eventName) {
    getCSQGlobal().push(['trackPageEvent', eventName]);
}
/**
 * Sends a dynamic variable to Contentsquare for session segmentation.
 * Unlike custom variables, dynamic variables can be sent at any point without requiring a pageview.
 * Strings support regex matching; numbers support range operators in the CS app.
 * @see https://docs.contentsquare.com/en/web/sending-dynamic-vars/
 */
export function csqTrackDynamicVariable(key, value) {
    getCSQGlobal().push(['trackDynamicVariable', { key, value }]);
}
/**
 * Sends an artificial pageview to Contentsquare for tracking modals/popups
 * @see: https://docs.contentsquare.com/en/web/artificial-pageviews/#tracking-a-modal-opening
 * @param action - The action to track ("open" or "close")
 * @param popupIdentifier - The identifier for the popup (e.g., "quickshop", "inline-cart", "size-guide", "post-add-to-cart")
 */
export function csqArtificialPageview(action, popupIdentifier) {
    const _uxa = getCSQGlobal();
    console.log("csqArtificialPageview", action, popupIdentifier);
    if (action === "open") {
        _uxa.push([
            "setQuery",
            window.location.search + (window.location.search ? "&" : "?") + `${popupIdentifier}`,
            { lifespan: 'onNextPageviewOnly' }
        ]);
        _uxa.push([
            "trackPageview",
            window.location.pathname + window.location.hash.replace("#", "?__"),
            { lifespan: 'onNextPageviewOnly' }
        ]);
    }
    else {
        _uxa.push([
            "trackPageview",
            window.location.pathname + window.location.hash.replace("#", "?__")
        ]);
    }
}
export const _forTesting = {
    addDyInfo
};
/**
 * converts a DY string to a readable format (sometimes the string is encoded)
 * @param string - the string to convert
 * @returns the converted string
 */
function convertDyString(string) {
    if (!string)
        return null;
    try {
        return decodeURIComponent(string);
    }
    catch (e) { // eslint-disable-line @typescript-eslint/no-unused-vars
        return string;
    }
}
